import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import utilities, { filterDataUtility, passesFilter } from "./UtilityFunctions"; // or wherever your utility functions live
import fields from "./fields";
import { consumeSheetFocus } from "./sheetFocus";

/**
 * A sheet's sort, filter and modal state.
 *
 * `sheetName` is optional only so that a caller which does not want the
 * dashboard's click-through behaviour can leave it out. Every real sheet passes
 * it; without it, a focus meant for this sheet is simply never claimed.
 */
export default function useTabularDataManager(dataArray, setData, sheetName) {
    //console.log("First car",dataArray[0]);
    const [modalVisible, setModalVisibility] = useState({ visible: false, mode: "add" });
    const [messagesModalVisible, setMessagesModalVisible] = useState(false);
    const [sortOrder, setSortOrder] = useState(() => {
        const sortObj = {};
        dataArray && dataArray[0] && Object.keys(dataArray[0]).forEach((propertyName) => {
        sortObj[propertyName] = "none";
        });
        return sortObj;
    });

    /*
     * A DASHBOARD CARD CAN ARRIVE HERE WITH A FILTER ALREADY CHOSEN.
     *
     * Pressing "22 accounts past their due date" navigates to this sheet and
     * wants it showing those twenty-two. sheetFocus.js explains why that travels
     * as a module-level one-shot rather than an event or a prop.
     *
     * IT IS CLAIMED IN THE INITIALISER, NOT IN AN EFFECT. The filters have to be
     * in place for the FIRST render: seeded from an effect, the sheet paints
     * once unfiltered and then again filtered, which on a large sheet is a
     * visible flash of rows the owner did not ask to see.
     *
     * A useState initialiser runs once per mount even under StrictMode's double
     * invocation, because React reuses the first result — and consumeSheetFocus
     * is idempotent after the first call anyway, returning null thereafter.
     */
    const focus = useRef(null);
    const [filterObject, setFilterObject] = useState(() => {
        const claimed = consumeSheetFocus(sheetName);
        if (!claimed) return {};
        focus.current = claimed;
        return Object.fromEntries((claimed.filters || []).map(
            ({ field, filterType, lowerValue, upperValue }) =>
                [field, { filterType, lowerValue, upperValue }]
        ));
    });

    /*
     * `direction` is optional, and omitting it keeps the toggle every column
     * header has always had. It exists for the focus below, which knows which
     * way it wants the sheet sorted and must not have to reason about what the
     * previous state was — calling the toggle twice to reach "desc" would depend
     * on nobody having sorted that column first.
     */
    const sortData = useCallback((field, direction) => {
        setSortOrder(prev => {
        const next = direction || (prev[field] === "asc" ? "desc" : "asc");
        setData(prevData => utilities.sortArray(prevData, field, next));
        return { ...prev, [field]: next };
        });
    }, [setData]);

    /*
     * The focus's SORT waits for rows; its filters did not.
     *
     * sortArray works on the data in hand, so applying it at mount does nothing
     * at all — the sheet routinely mounts before its fetch lands, which is the
     * normal case now that sheets load per tab rather than all at login. So this
     * waits for the first non-empty array and then fires once.
     *
     * The ref guard is what makes it once: `dataArray` changes identity on every
     * refetch, and re-sorting on each would silently undo a column sort the owner
     * had chosen in the meantime.
     */
    const sorted = useRef(false);
    useEffect(() => {
        if (sorted.current) return;
        const sort = focus.current?.sort;
        if (!sort || !dataArray?.length) return;
        sorted.current = true;
        sortData(sort.field, sort.direction);
    }, [dataArray, sortData]);

    const filterData = useCallback((fieldName, filterType, lowerValue, upperValue) => {
        filterDataUtility(setFilterObject, fieldName, filterType, lowerValue, upperValue);
    }, []);

    //Memoized on the two things it actually depends on. Unmemoized, this walked
    //every key of every row on every render of the sheet — a dialog opening
    //re-filtered thousands of rows — and handed back a fresh array identity that
    //invalidated every downstream memo keyed on it.
    const filteredDataArray = useMemo(() => {
        //Only the filtered columns need checking, and there are usually none.
        //The old version iterated all ~40 keys of every row to discover that.
        const activeKeys = Object.keys(filterObject).filter(key => filterObject[key]);
        if (activeKeys.length === 0) return dataArray;

        return dataArray.filter((rowObj) =>
            activeKeys.every(key =>
                //A filter can outlive the column it was set on (sheets differ in
                //shape); an unknown field can't be evaluated, so it can't exclude.
                !(key in rowObj) || !fields[key]
                    ? true
                    : passesFilter(rowObj[key], filterObject[key], fields[key].dataType)
            )
        );
    }, [dataArray, filterObject]);

    return {
        modalVisible, setModalVisibility,
        messagesModalVisible, setMessagesModalVisible,
        sortOrder, sortData,
        filterObject, filterData,
        filteredDataArray
    };
}
